import { createWithEqualityFn } from 'zustand/traditional'
import { devtools, persist } from 'zustand/middleware'
import { logger } from '@loc/helpers/src/logger/client'
import { shallow } from 'zustand/vanilla/shallow'
import { produce } from 'immer'
import { message } from 'antd'

import { AIMusicGeneratorStoreName } from '../_types'
import { filterNewLine } from '../utils'

import { type State, initialState } from './initialState'

import FetchStreamService from '@/helpers/FetchStreamService'
import { CUSTOM_EVENTS } from '@/constants/events'
import { throwGlobalServerError } from '@/utils/error'

export enum GenerateLyricsSmartType {
  LYRICS = 'lyrics', // 智能生成歌词
  POLISH = 'polish', // 根据ai给的歌词优化建议，生成新的歌词
}

interface GenerateLyricsSmartParams {
  type: GenerateLyricsSmartType
  formType?: 'smart' | 'custom'
  prompt: string
  thinking: boolean
  imageUrl?: string
  channelId?: string
  lyrics?: string
  polishPrompt?: string
  generateLyricsFinishCallback?: (generatedLyrics?: string) => void
  updateLyricsRealTimeCallback?: (lyrics: string) => void
}

interface Action {
  updateState: <K extends keyof State>(key: K, value: State[K]) => void
  updateStates: (updates: Partial<State>) => void
  generateLyricsSmart: (data: GenerateLyricsSmartParams) => Promise<void>
  updatelyricsTextareaContent: (lyrics: string) => void
  updateAiIdeasModalOpen: (open: boolean) => void
  updateMoreIdeasModalOpen: (open: boolean) => void
  setIsStream: (isStream: boolean) => void
  stopGenerateLyrics: () => Promise<void>
  updateAiPolishPromptContent: (aiPolishPromptContent: string[]) => void
  updateAiIdeasAreaShow: (show: boolean) => void
  updateAiThinkingAreaShow: (show: boolean) => void
  updateAiLyriceAreaShow: (show: boolean) => void
  updateAiThinkingContentExpand: (expand: boolean) => void
  updateAiCreateBtnShow: (show: boolean) => void
  updateLyricsCustomTextareaContent: (content: string) => void
}

export type Store = State & Action

const triggerAutoUpdateSubUsage = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.AUTO_UPDATE_SUB_USAGE))
  }, 100)
}

// 使用zustand做状态管理
export const useAiMusicGeneratorStoreFromZustand =
  createWithEqualityFn<Store>()(
    devtools(
      persist(
        (set, get) => ({
          ...initialState,
          // 原子类型
          updateState: (key, value) =>
            set(
              (state) => ({
                ...state,
                [key]: value,
              }),
              false,
              `updateState-${key}`,
            ),
          // 批量更新函数
          updateStates: (updates) =>
            set((state) => ({
              ...state,
              ...updates,
            })),

          // 更新isStream状态
          setIsStream: (isStream) => set((state) => ({ ...state, isStream })),

          // 更新aiIdeasModal显示隐藏状态
          updateAiIdeasModalOpen: (open) => {
            const { updateState } = get()
            updateState('aiIdeasModalOpen', open)
          },

          // 更新moreIdeasModal显示隐藏状态
          updateMoreIdeasModalOpen: (open) => {
            const { updateState } = get()
            updateState('moreIdeasModalOpen', open)
          },

          // 更新歌词
          updatelyricsTextareaContent: (lyrics) =>
            set(
              produce((state) => {
                state.lyricsTextareaContent = lyrics
              }),
            ),

          // 设置ai给的歌词优化建议
          updateAiPolishPromptContent: (aiPolishPromptContent) =>
            set(
              produce((state) => {
                state.aiPolishPromptContent = aiPolishPromptContent
              }),
            ),

          // 通过ai大模型智能生成歌词
          generateLyricsSmart: async (params) => {
            const { isStream, updateState, updateStates } = get()

            const fetchStreamService = new FetchStreamService(
              '/api/generation/lyric',
            )
            let reasoningContent = ''
            let lyricsContent = ''
            let thinkingTimeinterval: NodeJS.Timeout | null = null
            let isFirstLyrics = true
            const abortController = new AbortController()
            // 表单模式
            const { formType = 'smart' } = params
            updateState('lyricsGeneratorAbortController', abortController)
            updateStates({ isStream: true })

            try {
              // 优化歌词的情况下，必须有歌词
              if (
                params.type === GenerateLyricsSmartType.POLISH &&
                !params.lyrics
              ) {
                message.info('Please enter lyrics')
                return
              }

              // 歌词长度超过最大长度
              // if (
              //   params.lyrics &&
              //   params.lyrics.length > AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH
              // ) {
              //   message.info(
              //     `prompt length exceeds the maximum length, please enter less than ${AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH} characters`,
              //   )
              //   return
              // }

              // 智能生成歌词，清空思考内容，倒计时以及歌词
              if (
                params.type === GenerateLyricsSmartType.LYRICS &&
                params.thinking
              ) {
                updateState('thinkingTime', 0)
                updateState('thinkingTimeinterval', null)
                updateState('aiThinkingAreaShow', true)
                if (thinkingTimeinterval) {
                  clearInterval(thinkingTimeinterval)
                }
                set(
                  produce((state) => {
                    state.aiThinkingInfo.reasoningContent.content = ''
                  }),
                )
                updateState('lyricsTextareaContent', '')
                updateStates({
                  aiLyriceAreaShow: false,
                  styleShow: false,
                  aiCreateBtnShow: false,
                })
              }

              // 只在第一次没计时器的时候开始计时
              if (
                !thinkingTimeinterval &&
                params.type === GenerateLyricsSmartType.LYRICS &&
                params.thinking
              ) {
                thinkingTimeinterval = setInterval(() => {
                  const { thinkingTime: currentTime } = get()
                  updateState('thinkingTime', currentTime + 1)
                }, 1000)
                updateState('thinkingTimeinterval', thinkingTimeinterval)
              }

              await fetchStreamService.fetchWithSSE({
                // 去除type字段
                payload: {
                  ...params,
                  prompt: params.prompt || undefined,
                  type: undefined,
                },
                controller: abortController,
                // 处理普通事件
                onEvent: (event) => {},
                // 处理自定义事件
                onCustomEvent: (event) => {
                  const {
                    type,
                    data: { content },
                  } = event as any

                  // channelId记录
                  if (type === 'channel' && content) {
                    logger.log('channelId', content)
                    set(
                      produce((state) => {
                        state.aiThinkingInfo.channelId = content
                      }),
                    )
                  }

                  //  思考内容
                  if (type === 'reasoning' && content) {
                    reasoningContent = reasoningContent + content
                    // 用immer里的produce更新最新content 字段
                    set(
                      produce((state) => {
                        state.aiThinkingInfo.reasoningContent.content =
                          reasoningContent
                        if (!state.aiThinkingContentExpand) {
                          state.aiThinkingContentExpand = true
                        }
                      }),
                    )
                  }

                  // 歌词内容
                  if (type === 'lyric' && content) {
                    lyricsContent = lyricsContent + filterNewLine(content)
                    set(
                      produce((state) => {
                        // 歌词生成后，自动收起思考内容，只有第一次的时候才收起
                        if (state.aiThinkingContentExpand && isFirstLyrics) {
                          isFirstLyrics = false
                          state.aiThinkingContentExpand = false
                        }

                        if (formType === 'custom') {
                          state.lyricsCustomTextareaContent = lyricsContent
                        } else {
                          state.aiLyriceAreaShow = true
                          state.lyricsTextareaContent = lyricsContent
                        }
                      }),
                    )

                    if (thinkingTimeinterval) {
                      clearInterval(thinkingTimeinterval)
                      updateState('thinkingTimeinterval', null)
                    }

                    // 实时更新歌词的回调，需要在 produce 外部调用
                    params.updateLyricsRealTimeCallback?.(lyricsContent)
                  }

                  // 事件完成
                  if (type === 'polish_prompt') {
                    // 将ai思考返回的歌词内容赋值给歌词文本框的内容
                    set(
                      produce((state) => {
                        // state.lyricsTextareaContent =
                        //   state.aiThinkingInfo.lyricsContent.content
                        state.polishPrompt = content
                        state.isStream = false
                        state.styleShow = true
                      }),
                    )
                    params.generateLyricsFinishCallback?.(lyricsContent)
                    triggerAutoUpdateSubUsage()
                  }

                  // 异常事件处理
                  if (
                    type === 'timeout' ||
                    type === 'error' ||
                    type === 'abort'
                  ) {
                    const { setIsStream, updateState } = get()
                    message.error(content)
                    setIsStream(false)
                    if (thinkingTimeinterval) {
                      clearInterval(thinkingTimeinterval)
                      updateState('thinkingTimeinterval', null)
                    }
                  }
                },
                // 处理生成错误
                onGeneratingError: (errorCode, errorMessage) => {
                  logger.dev.log(
                    'generateLyricsSmart onGeneratingError',
                    errorCode,
                    errorMessage,
                  )
                  throwGlobalServerError(errorCode, errorMessage)
                },
              })
            } catch (error) {
              const { setIsStream } = get()
              setIsStream(false)
            } finally {
              const { setIsStream } = get()
              setIsStream(false)
              if (thinkingTimeinterval) {
                clearInterval(thinkingTimeinterval)
                updateState('thinkingTimeinterval', null)
              }
            }
          },

          // 中断智能歌词生成的过程
          stopGenerateLyrics: async () => {
            const {
              lyricsGeneratorAbortController,
              setIsStream,
              aiThinkingInfo,
              thinkingTimeinterval,
              updateState,
            } = get()

            // 该接口不是trpc接口
            await fetch('/api/generation/lyric/abort', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                channelId: aiThinkingInfo.channelId,
              }),
            })
              .then((res) => {
                setIsStream(false)
              })
              .catch((err) => {
                logger.dev.log('stopGenerateLyrics err', err)
                if (thinkingTimeinterval) {
                  clearInterval(thinkingTimeinterval)
                  updateState('thinkingTimeinterval', null)
                }
              })
              .finally(() => {
                setIsStream(false)
                if (thinkingTimeinterval) {
                  clearInterval(thinkingTimeinterval)
                  updateState('thinkingTimeinterval', null)
                }
                lyricsGeneratorAbortController?.abort()
              })
          },

          // 更新ai建议区域是否展示
          updateAiIdeasAreaShow: (show) => {
            const { updateState } = get()
            updateState('aiIdeasAreaShow', show)
          },

          // 更新ai思考区域是否展示
          updateAiThinkingAreaShow: (show) => {
            const { updateState } = get()
            updateState('aiThinkingAreaShow', show)
          },

          // 更新ai歌词区域是否展示
          updateAiLyriceAreaShow: (show) => {
            const { updateState } = get()
            updateState('aiLyriceAreaShow', show)
          },

          // 更新ai思考内容是否展开
          updateAiThinkingContentExpand: (expand) => {
            const { updateState } = get()
            updateState('aiThinkingContentExpand', expand)
          },

          // 更新ai创建按钮是否展示
          updateAiCreateBtnShow: (show) => {
            const { updateState } = get()
            updateState('aiCreateBtnShow', show)
          },

          // 更新自定义歌词文本框内容
          updateLyricsCustomTextareaContent: (content) => {
            const { updateState } = get()
            updateState('lyricsCustomTextareaContent', content)
          },
        }),
        {
          name: AIMusicGeneratorStoreName,
          partialize: (state) => {},
        },
      ),
      { name: AIMusicGeneratorStoreName },
    ),
    shallow,
  )
